/**
 * React Query Hooks for Dashboard Data
 * 
 * Custom hooks using React Query for fetching and caching dashboard metrics
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
    dashboardApi,
    OverallMetrics,
    EspPerformanceResponse,
    TagDistributionResponse,
    CampaignHistoryResponse,
    SubscriberGrowthResponse,
} from '../services/dashboardApi';
import { useMutation, useQueryClient } from '@tanstack/react-query';

// ============================================
// Query Keys
// ============================================

export const queryKeys = {
    overall: (geo: string, esp: string) => ['dashboard', 'overall', geo, esp] as const,
    espPerformance: (geo: string, days: number, esp: string) => ['dashboard', 'esp-performance', geo, days, esp] as const,
    tagDistribution: (geo: string, esp: string) => ['dashboard', 'tag-distribution', geo, esp] as const,
    campaignHistory: (geo: string, esp: string, limit: number, esp_id?: string) =>
        ['dashboard', 'campaign-history', geo, esp, limit, esp_id] as const,
    subscriberGrowth: (geo: string, period: string) =>
        ['dashboard', 'subscriber-growth', geo, period] as const,
    segmentationConfig: (geo: string, espId: string) =>
        ['dashboard', 'segmentation-config', geo, espId] as const,
};

// ============================================
// Custom Hooks
// ============================================

/**
 * Hook to fetch overall dashboard metrics
 */
export function useOverallMetrics(
    geo: string = 'no',
    esp: string = 'gr'
): UseQueryResult<OverallMetrics, Error> {
    return useQuery({
        queryKey: queryKeys.overall(geo, esp),
        queryFn: () => dashboardApi.getOverallMetrics(geo, esp),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchInterval: 10 * 60 * 1000, // Auto-refetch every 10 minutes
        enabled: !!geo,
    });
}

/**
 * Hook to fetch ESP performance metrics
 */
export function useEspPerformance(
    geo: string = 'no',
    days: number = 30,
    esp: string = 'gr'
): UseQueryResult<EspPerformanceResponse, Error> {
    return useQuery({
        queryKey: queryKeys.espPerformance(geo, days, esp),
        queryFn: () => dashboardApi.getEspPerformance(geo, days, esp),
        staleTime: 5 * 60 * 1000,
        refetchInterval: 10 * 60 * 1000,
        enabled: !!geo,
    });
}

/**
 * Hook to fetch tag distribution
 */
export function useTagDistribution(
    geo: string = 'no',
    esp: string = 'gr'
): UseQueryResult<TagDistributionResponse, Error> {
    return useQuery({
        queryKey: queryKeys.tagDistribution(geo, esp),
        queryFn: () => dashboardApi.getTagDistribution(geo, esp),
        staleTime: 5 * 60 * 1000,
        refetchInterval: 10 * 60 * 1000,
        enabled: !!geo,
    });
}

/**
 * Hook to fetch campaign history
 */
export function useCampaignHistory(
    geo: string = 'no',
    esp: string = 'gr',
    limit: number = 10,
    esp_id?: string
): UseQueryResult<CampaignHistoryResponse, Error> {
    return useQuery({
        queryKey: queryKeys.campaignHistory(geo, esp, limit, esp_id),
        queryFn: () => dashboardApi.getCampaignHistory(geo, esp, limit, esp_id),
        staleTime: 2 * 60 * 1000, // 2 minutes (campaigns change more frequently)
        refetchInterval: 5 * 60 * 1000,
        enabled: !!geo,
    });
}

/**
 * Hook to fetch subscriber growth
 */
export function useSubscriberGrowth(
    geo: string = 'no',
    period: '7d' | '30d' | '90d' | '1y' = '30d'
): UseQueryResult<SubscriberGrowthResponse, Error> {
    return useQuery({
        queryKey: queryKeys.subscriberGrowth(geo, period),
        queryFn: () => dashboardApi.getSubscriberGrowth(geo, period),
        staleTime: 5 * 60 * 1000,
        refetchInterval: 10 * 60 * 1000,
        enabled: !!geo,
    });
}

/**
 * Hook to fetch segmentation configuration
 */
export function useSegmentationConfig(
    geo: string,
    espId: string
) {
    return useQuery({
        queryKey: queryKeys.segmentationConfig(geo, espId),
        queryFn: () => dashboardApi.getSegmentationConfig(geo, espId),
        staleTime: 5 * 60 * 1000,
        enabled: !!geo && !!espId,
    });
}

/**
 * Hook to update segmentation configuration
 */
export function useUpdateSegmentationConfig() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ geo, espId, config }: { geo: string; espId: string; config: any }) =>
            dashboardApi.saveSegmentationConfig(geo, espId, config),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.segmentationConfig(variables.geo, variables.espId)
            });
        },
    });
}
